import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { LATEST_TXN_DATES_QUERY, OCCUPANCY_TRANSACTIONS_QUERY, STORES_DETAIL_QUERY } from '../graphql/queries';
import { useSelectedStore } from '../hooks/useSelectedStore';
import { gql } from '../lib/api';
import { airRegiAccountHour, airRegiBusinessDate, airRegiService } from '../lib/airRegiBusinessTime';
import { type StoreLD } from '../lib/salesAnalytics';
import { calculateOccupancyDay, type StayTransaction } from '../lib/stayOccupancy';
import { eventFor, useCalendarEvents } from '../hooks/useCalendarEvents';

interface Store extends StoreLD {
  name: string;
  seatCount: number | null;
  airregiBusinessDayStart: string | null;
}

interface Transaction extends StayTransaction {
  storeId: string;
}

type Segment = 'all' | 'lunch' | 'dinner';
type PeriodWeeks = 1 | 4;

interface DisplayOption {
  key: string;
  store: Store;
  segment: Segment;
  label: string;
}

interface DaySample {
  date: string;
  rate: number;
  occupied: number;
}

interface HeatCell {
  hour: number;
  weekdayIndex: number;
  rate: number | null;
  samples: DaySample[];
}

const DAY_LABELS = ['火', '水', '木', '金', '土', '日'] as const;
const toDate = (date: string): Date => new Date(`${date}T00:00:00Z`);
const addDays = (date: string, amount: number): string => {
  const next = toDate(date);
  next.setUTCDate(next.getUTCDate() + amount);
  return next.toISOString().slice(0, 10);
};
const previousSunday = (date: string): string => addDays(date, -toDate(date).getUTCDay());
const todayInJapan = (): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};
const longDate = (date: string): string => {
  const [year, month, day] = date.split('-');
  return `${year}年${month}月${day}日`;
};
const validDate = (value: string | null): value is string =>
  /^\d{4}-\d{2}-\d{2}$/.test(value ?? '') && !Number.isNaN(toDate(value!).getTime())
  && toDate(value!).toISOString().slice(0, 10) === value;
const pct = (value: number | null): string => value == null ? 'データなし' : `${value.toFixed(1)}%`;
const cellPct = (value: number): string => value > 0 && value < 0.5 ? '<1%' : value > 100 && value < 100.5 ? '100%超' : `${Math.round(value)}%`;
const hourText = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;

const colorClass = (value: number | null): string => {
  if (value == null) return 'kpi-heat-empty';
  if (value === 0) return 'kpi-heat-zero';
  if (value < 20) return 'kpi-heat-1';
  if (value < 40) return 'kpi-heat-2';
  if (value < 60) return 'kpi-heat-3';
  if (value < 80) return 'kpi-heat-4';
  if (value <= 100) return 'kpi-heat-5';
  return 'kpi-heat-over';
};

const displayOptionsFor = (stores: Store[]): DisplayOption[] =>
  [...stores]
    .sort((a, b) => Number(!!b.lunchStart) - Number(!!a.lunchStart))
    .flatMap((store): DisplayOption[] => store.lunchStart && store.lunchEnd
      ? [
        { key: `${store.id}:lunch`, store, segment: 'lunch', label: `${store.name}（ランチ）` },
        { key: `${store.id}:dinner`, store, segment: 'dinner', label: `${store.name}（ディナー）` },
      ]
      : [{ key: `${store.id}:all`, store, segment: 'all', label: store.name }]);

export const OccupancyHeatmapPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedStoreId, setStoredStoreId] = useSelectedStore();
  const initialStoreId = searchParams.get('store') || storedStoreId;
  const initialSegment = searchParams.get('segment');
  const initialDate = searchParams.get('date');
  const [selectedKey, setSelectedKey] = useState(
    `${initialStoreId}:${initialSegment === 'lunch' || initialSegment === 'dinner' ? initialSegment : 'all'}`,
  );
  const [selectedDate, setSelectedDate] = useState(validDate(initialDate) ? initialDate : '');
  const [periodWeeks, setPeriodWeeks] = useState<PeriodWeeks>(searchParams.get('weeks') === '1' ? 1 : 4);
  const [selectedCell, setSelectedCell] = useState<{ weekdayIndex: number; hour: number } | null>(null);

  const storesQ = useQuery({
    queryKey: ['storesDetail'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_DETAIL_QUERY),
  });
  const stores = useMemo(() => storesQ.data?.stores ?? [], [storesQ.data?.stores]);
  const displayOptions = useMemo(() => displayOptionsFor(stores), [stores]);
  const requestedStoreId = selectedKey.slice(0, selectedKey.lastIndexOf(':'));
  const selectedOption = displayOptions.find((option) => option.key === selectedKey)
    ?? displayOptions.find((option) => option.store.id === requestedStoreId)
    ?? displayOptions[0];
  const storeId = selectedOption?.store.id ?? '';
  const segment = selectedOption?.segment ?? 'all';
  const seatCount = selectedOption?.store.seatCount ?? null;

  useEffect(() => {
    if (!selectedOption) return;
    if (selectedKey !== selectedOption.key) setSelectedKey(selectedOption.key);
    setStoredStoreId(storeId);
  }, [selectedKey, selectedOption, setStoredStoreId, storeId]);

  useEffect(() => {
    if (!storeId) return;
    const next = new URLSearchParams(searchParams);
    next.set('store', storeId);
    if (segment === 'all') next.delete('segment');
    else next.set('segment', segment);
    if (selectedDate) next.set('date', selectedDate);
    else next.delete('date');
    if (periodWeeks === 4) next.delete('weeks');
    else next.set('weeks', String(periodWeeks));
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [periodWeeks, searchParams, segment, selectedDate, setSearchParams, storeId]);

  const today = useMemo(todayInJapan, []);
  const latestQ = useQuery({
    enabled: !!storeId,
    queryKey: ['occupancyHeatmapLatest', storeId, segment, stores, today],
    queryFn: async () => {
      for (let offset = 0; offset <= 60; offset += 7) {
        const days = await Promise.all(Array.from({ length: Math.min(7, 61 - offset) }, (_, index) => {
          const date = addDays(today, -offset - index);
          return gql<{ transactions: Pick<Transaction, 'storeId' | 'visitDate' | 'visitTime' | 'accountDate' | 'accountTime'>[] }>(
            LATEST_TXN_DATES_QUERY, { storeId, from: date, to: date },
          );
        }));
        const store = stores.find((item) => item.id === storeId);
        const matching = days.flatMap((day) => day.transactions).filter((transaction) =>
          segment === 'all' || airRegiService(transaction, store) === segment);
        if (matching.length) return matching.map((transaction) =>
          airRegiBusinessDate(transaction, store?.airregiBusinessDayStart)).sort().at(-1) ?? '';
      }
      return '';
    },
  });
  const latestDate = latestQ.data ?? '';
  const latestCompleteSunday = latestDate ? previousSunday(latestDate === today ? addDays(latestDate, -1) : latestDate) : '';

  useEffect(() => {
    if (!selectedDate && latestCompleteSunday) setSelectedDate(latestCompleteSunday);
  }, [latestCompleteSunday, selectedDate]);

  const range = useMemo(() => {
    if (!selectedDate) return null;
    const to = previousSunday(selectedDate);
    return { from: addDays(to, -((periodWeeks - 1) * 7 + 5)), to };
  }, [periodWeeks, selectedDate]);
  const eventsQ = useCalendarEvents(range?.from, range?.to);
  const transactionsQ = useQuery({
    enabled: !!storeId && !!range,
    queryKey: ['occupancyHeatmapTransactions', storeId, range?.from, range?.to],
    queryFn: async () => {
      const transactions: Transaction[] = [];
      const daysToFetch = periodWeeks * 7;
      for (let offset = 0; offset < daysToFetch; offset += 7) {
        const days = await Promise.all(Array.from({ length: Math.min(7, daysToFetch - offset) }, (_, index) => {
          const date = addDays(range!.from, offset + index);
          return gql<{ transactions: Transaction[] }>(OCCUPANCY_TRANSACTIONS_QUERY, { storeId, from: date, to: date });
        }));
        transactions.push(...days.flatMap((day) => day.transactions));
      }
      return { transactions };
    },
  });

  const hours = useMemo(() => {
    if (!selectedOption) return [];
    const store = selectedOption.store;
    const startTime = segment === 'lunch' ? store.lunchStart : store.dinnerStart;
    const endTime = segment === 'lunch' ? store.lunchEnd : store.dinnerEnd;
    const start = startTime ? Number(startTime.split(':')[0]) : 0;
    const configuredEnd = endTime ? Math.ceil(Number(endTime.split(':')[0]) + Number(endTime.split(':')[1]) / 60) : 24;
    const matching = (transactionsQ.data?.transactions ?? []).filter((transaction) =>
      segment === 'all' || airRegiService(transaction, store) === segment);
    const latestAccountHour = Math.max(configuredEnd - 1, ...matching
      .map((transaction) => airRegiAccountHour(transaction, store.airregiBusinessDayStart))
      .filter((hour): hour is number => hour !== null));
    return Array.from({ length: Math.max(0, latestAccountHour + 3 - start) }, (_, index) => start + index);
  }, [segment, selectedOption, transactionsQ.data]);

  const daily = useMemo(() => {
    if (!range || !hours.length) return [];
    const store = stores.find((item) => item.id === storeId);
    const matching = (transactionsQ.data?.transactions ?? []).filter((transaction) =>
      segment === 'all' || airRegiService(transaction, store) === segment);
    return Array.from({ length: periodWeeks }, (_, weekIndex) => DAY_LABELS.map((_, weekdayIndex) => {
      const date = addDays(range.from, weekIndex * 7 + weekdayIndex);
      return {
        date, weekdayIndex,
        occupancy: calculateOccupancyDay(eventFor(eventsQ.data?.calendarEvents, storeId, date) ? [] : matching, date, hours[0], hours.at(-1)! + 1, seatCount, store?.airregiBusinessDayStart ?? '05:00'),
      };
    })).flat();
  }, [eventsQ.data, hours, periodWeeks, range, seatCount, segment, storeId, stores, transactionsQ.data]);

  const cells = useMemo<HeatCell[][]>(() => DAY_LABELS.map((_, weekdayIndex) => hours.map((hour, hourIndex) => {
    const samples = daily.filter((day) => day.weekdayIndex === weekdayIndex && day.occupancy.reliableOccupancy)
      .flatMap((day): DaySample[] => {
        const point = day.occupancy.points[hourIndex];
        return point?.occupancyRate != null && point.occupied != null
          ? [{ date: day.date, rate: point.occupancyRate, occupied: point.occupied }]
          : [];
      });
    return { weekdayIndex, hour, samples,
      rate: samples.length ? samples.reduce((sum, sample) => sum + sample.rate, 0) / samples.length : null };
  })), [daily, hours]);
  const availableDays = daily.filter((day) => day.occupancy.reliableOccupancy).length;
  const allRates = cells.flat().flatMap((cell) => cell.rate == null ? [] : [cell.rate]);
  const averageRate = allRates.length ? allRates.reduce((sum, rate) => sum + rate, 0) / allRates.length : null;
  const peakCell = cells.flat().filter((cell) => cell.rate != null)
    .sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0))[0];
  const inspectedCell = selectedCell
    ? cells[selectedCell.weekdayIndex]?.find((cell) => cell.hour === selectedCell.hour)
    : null;
  const lineGraphUrl = `/kpi/stay-occupancy?store=${encodeURIComponent(storeId)}${segment === 'all' ? '' : `&segment=${segment}`}${range ? `&date=${range.to}` : ''}`;
  const isLoading = storesQ.isLoading || latestQ.isLoading || transactionsQ.isLoading || eventsQ.isLoading;
  const hasError = storesQ.isError || latestQ.isError || transactionsQ.isError || eventsQ.isError;

  return (
    <div className="kpi-weekly-page kpi-heat-page">
      <header className="kpi-weekly-heading">
        <div>
          <div className="kpi-weekly-kicker">KPI LAB / SEAT UTILIZATION</div>
          <h1>座席稼働率ヒートマップ</h1>
          <p>曜日と時間帯ごとの座席の埋まり具合を、Airレジの滞在記録から推定して比較します。</p>
        </div>
        <label className="kpi-weekly-store">
          <span>表示店舗・時間帯</span>
          <select value={selectedOption?.key ?? ''} onChange={(event) => { setSelectedKey(event.target.value); setSelectedCell(null); }}>
            {displayOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
          </select>
        </label>
      </header>

      <div className="kpi-weekly-note">
        <span>KPI検討用</span>
        <p>Airレジ営業日と店舗マスタの集計終点で分類し、伝票作成から会計までの「1時間平均在席人数 ÷ 座席数」を表示します。時間軸は最終会計から2時間先まで延ばし、イベント日と滞在情報が80%未満の日は除外します。0%とデータなしは分けて表示します。</p>
      </div>

      <div className="kpi-weekly-range-controls kpi-heat-controls">
        <label>
          <span>対象期間の基準日</span>
          <input type="date" value={selectedDate} onChange={(event) => { setSelectedDate(event.target.value); setSelectedCell(null); }} />
        </label>
        <label>
          <span>集計期間</span>
          <select value={periodWeeks} onChange={(event) => { setPeriodWeeks(Number(event.target.value) as PeriodWeeks); setSelectedCell(null); }}>
            <option value={4}>4営業週の平均</option>
            <option value={1}>1営業週</option>
          </select>
        </label>
        <div className="kpi-weekly-range-actions">
          <button type="button" disabled={!range} onClick={() => { setSelectedDate(addDays(range!.to, -periodWeeks * 7)); setSelectedCell(null); }}>← 前の期間</button>
          <button type="button" disabled={!range} onClick={() => { setSelectedDate(addDays(range!.to, periodWeeks * 7)); setSelectedCell(null); }}>次の期間 →</button>
          <button type="button" disabled={!latestCompleteSunday} onClick={() => { setSelectedDate(latestCompleteSunday); setSelectedCell(null); }}>最新期間</button>
        </div>
      </div>

      {hasError && <section className="kpi-weekly-state" role="alert">データを取得できませんでした。時間をおいて再度お試しください。</section>}
      {!hasError && isLoading && <section className="kpi-weekly-state">座席稼働率を集計しています…</section>}
      {!hasError && !isLoading && !latestDate && !selectedDate && (
        <section className="kpi-weekly-state">直近60日間に会計データがありません。基準日を指定すると過去の期間を表示できます。</section>
      )}
      {!hasError && !isLoading && range && (
        <>
          <section className="kpi-weekly-overview">
            <div className="kpi-weekly-period">
              <span>対象営業期間</span>
              <strong>{longDate(range.from)} — {longDate(range.to)}</strong>
              <small>{selectedOption?.label} ／ 座席数 {seatCount != null ? `${seatCount}席` : '未登録'} ／ 有効 {availableDays} / {daily.length} 営業日</small>
            </div>
            <div className="kpi-weekly-total kpi-weekly-total-sales">
              <span>時間帯の平均稼働率</span>
              <strong>{pct(averageRate)}</strong>
              <small>表示できる曜日×時間帯の平均</small>
            </div>
            <div className="kpi-weekly-total kpi-weekly-total-customers">
              <span>最も高い曜日・時間帯</span>
              <strong>{peakCell ? `${DAY_LABELS[peakCell.weekdayIndex]} ${hourText(peakCell.hour)}` : 'データなし'}</strong>
              <small>{peakCell ? `${pct(peakCell.rate)} ／ ${peakCell.samples.length}日分` : '集計可能な伝票がありません'}</small>
            </div>
          </section>

          {seatCount == null && <p className="kpi-stay-alert">店舗マスタの座席数が未登録のため、座席稼働率を計算できません。</p>}

          <section className="kpi-heat-panel">
            <div className="kpi-heat-panel-heading">
              <div>
                <span>DAY × HOUR</span>
                <h2>曜日・時間帯別の座席稼働率</h2>
              </div>
              <Link to={lineGraphUrl}>滞在時間と人数のグラフを見る →</Link>
            </div>
            <div className="kpi-heat-legend" aria-label="座席稼働率の色分け">
              <span><i className="kpi-heat-zero" />0%</span>
              <span><i className="kpi-heat-1" />1–19%</span>
              <span><i className="kpi-heat-2" />20–39%</span>
              <span><i className="kpi-heat-3" />40–59%</span>
              <span><i className="kpi-heat-4" />60–79%</span>
              <span><i className="kpi-heat-5" />80–100%</span>
              <span><i className="kpi-heat-over" />100%超</span>
              <span><i className="kpi-heat-empty" />データなし</span>
            </div>

            <div className="kpi-heat-scroll" role="region" aria-label="座席稼働率ヒートマップ" tabIndex={0}>
              <table className="kpi-heat-table">
                <thead>
                  <tr>
                    <th scope="col">曜日</th>
                    {hours.map((hour) => <th scope="col" key={hour}>{hourText(hour)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {cells.map((row, weekdayIndex) => (
                    <tr key={DAY_LABELS[weekdayIndex]}>
                      <th scope="row">{DAY_LABELS[weekdayIndex]}<small>曜日</small></th>
                      {row.map((cell) => (
                        <td key={cell.hour}>
                          {cell.rate == null ? (
                            <span className="kpi-heat-cell kpi-heat-empty" aria-label={`${DAY_LABELS[weekdayIndex]}曜日 ${hourText(cell.hour)} データなし`}>—</span>
                          ) : (
                            <button type="button"
                              className={`kpi-heat-cell ${colorClass(cell.rate)}${inspectedCell === cell ? ' kpi-heat-selected' : ''}`}
                              aria-pressed={inspectedCell === cell}
                              aria-label={`${DAY_LABELS[weekdayIndex]}曜日 ${hourText(cell.hour)}、座席稼働率${pct(cell.rate)}、${cell.samples.length}日分`}
                              onClick={() => setSelectedCell({ weekdayIndex, hour: cell.hour })}>
                              <strong>{cellPct(cell.rate)}</strong>
                              <small>{cell.samples.length}日</small>
                            </button>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="kpi-heat-scroll-hint">画面幅が狭い場合は表を横にスクロールできます。マスを選ぶと集計した日付を確認できます。</p>
          </section>

          {inspectedCell && inspectedCell.rate != null && (
            <section className="kpi-heat-detail" aria-live="polite">
              <div>
                <span>SELECTED TIME SLOT</span>
                <h2>{DAY_LABELS[inspectedCell.weekdayIndex]}曜日 {hourText(inspectedCell.hour)}–{hourText(inspectedCell.hour + 1)}</h2>
                <p>座席稼働率の平均 <strong>{pct(inspectedCell.rate)}</strong> ／ 有効 {inspectedCell.samples.length} 日</p>
              </div>
              <ul>
                {inspectedCell.samples.map((sample) => (
                  <li key={sample.date}>
                    <span>{longDate(sample.date)}</span>
                    <strong>{pct(sample.rate)}</strong>
                    <small>平均在席 {sample.occupied.toFixed(1)}人</small>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
};
